import apiService from './ApiService';

// Servicio para operaciones relacionadas con usuarios
class UsuarioService {
  
  // Login de usuario
  async login(email, password) {
    try {
      const loginData = {
        email: email,
        password: password
      };

      // Usar el método especializado para login que maneja automáticamente errores 415
      const response = await apiService.postLogin('/User/login', loginData);
      
      // Si hay token, configurarlo en el servicio API
      if (response.token) {
        apiService.setAuthToken(response.token);
        
        // Debug inmediato para verificar que se guardó
        apiService.debugTokenStatus();
      }
      
      return response;
      
    } catch (error) {
      console.error('%c❌ ERROR EN LOGIN', 'color: #ef4444; font-weight: bold;');
      console.error(`%c│ Error: ${error.message}`, 'color: #ef4444;');
      throw error;
    }
  }

  // Logout de usuario
  async logout() {
    try {
      // Limpiar el token del servicio API
      apiService.setAuthToken(null);
      
      // Opcional: llamar al endpoint de logout en el backend si existe
      // await apiService.post('/User/logout');
      
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Aún así limpiar el token localmente
      apiService.setAuthToken(null);
      throw error;
    }
  }

  async selectRegional(regionalId) {
    if (!regionalId) {
      throw new Error('Debes seleccionar un regional.');
    }

    const response = await apiService.post('/User/seleccionar-regional', { idRegional: regionalId });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos seleccionar el regional.';
      throw new Error(msg);
    }

    return response;
  }

  // Obtener perfil del usuario actual
  async getProfile() {
    try {
      const response = await apiService.get('/User/profile', apiService.getAuthHeaders());
      
      return response;
      
    } catch (error) {
      console.error('Error al obtener perfil:', error);
      throw error;
    }
  }

  // Validar token actual
  async validateToken() {
    try {
      const response = await apiService.get('/User/validate', apiService.getAuthHeaders());
      return response;
    } catch (error) {
      console.error('Token inválido:', error);
      throw error;
    }
  }

  // Actualizar contraseña de usuario
  async updatePassword(currentPassword, newPassword, repeatNewPassword) {
    try {
      const passwordData = {
        currentPassword: currentPassword,
        newPassword: newPassword,
        repeatNewPassword: repeatNewPassword
      };

      const response = await apiService.put('/User/update-password', passwordData);
      
      return response;
    } catch (error) {
      console.error('%c❌ ERROR AL ACTUALIZAR CONTRASEÑA:', 'color: #ef4444; font-weight: bold;', error);
      
      // Mejorar los mensajes de error paral usuario
      if (error.message.includes('401')) {
        throw new Error('La contraseña actual es incorrecta');
      } else if (error.message.includes('400')) {
        throw new Error('Los datos de la contraseña no son válidos');
      } else if (error.message.includes('422')) {
        throw new Error('La nueva contraseña no cumple con los requisitos de seguridad');
      } else {
        throw new Error('Error al actualizar la contraseña. Inténtalo nuevamente.');
      }
    }
  }

  async getProfesoresPorRegional(regionalId, options = {}) {
    if (!regionalId) {
      throw new Error('No hay un regional activo seleccionado.');
    }

    try {
      const params = new URLSearchParams();
      if (options.start !== undefined && options.start !== null) params.append('start', options.start);
      if (options.end !== undefined && options.end !== null) params.append('end', options.end);
      const qs = params.toString();
      const response = await apiService.get(`/profesores/por-regional/${regionalId}${qs ? `?${qs}` : ''}`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener los profesores.';
        throw new Error(msg);
      }

      const profesores = response?.profesores || response?.Profesores || response?.data || response || [];
      const items = Array.isArray(profesores) ? profesores : [];
      const total = response?.total ?? response?.Total ?? items.length;
      if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
        return { items, total };
      }
      return items;
    } catch (error) {
      console.error('❌ Error al obtener profesores por regional:', error);

      if (error.message?.includes('404')) {
        throw new Error('No se encontraron profesores para este regional.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos cargar los profesores de este regional.');
    }
  }

  async updateProfesorActivo(profesorId, activo) {
    if (!profesorId) {
      throw new Error('No se encontró el identificador del profesor.');
    }

    try {
      const response = await apiService.put(`/profesores/${profesorId}/activo`, { activo });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el estado del profesor.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar estado del profesor:', error);

      if (error.message?.includes('404')) {
        throw new Error('El profesor no existe.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el estado del profesor.');
    }
  }

  async updateAlumnoActivo(alumnoId, activo) {
    if (!alumnoId) {
      throw new Error('No se encontró el identificador del alumno.');
    }

    try {
      const response = await apiService.put(`/alumnos/${alumnoId}/activo`, { activo });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el estado del alumno.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar estado del alumno:', error);

      if (error.message?.includes('404')) {
        throw new Error('El alumno no existe.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el estado del alumno.');
    }
  }

  async createProfesor(payload = {}) {
    if (!payload.idRegional) {
      throw new Error('Debes seleccionar un regional para crear el profesor.');
    }

    try {
      const response = await apiService.post('/profesores', payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos crear el profesor.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al crear profesor:', error);

      if (error.message?.includes('409')) {
        throw new Error('Ya existe un profesor con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos crear el profesor.');
    }
  }

  async updateProfesor(profesorId, payload = {}) {
    if (!profesorId) {
      throw new Error('No se encontró el identificador del profesor.');
    }
    if (!payload.idRegional) {
      throw new Error('Debes seleccionar un regional para actualizar el profesor.');
    }

    try {
      const response = await apiService.put(`/profesores/${profesorId}`, payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el profesor.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar profesor:', error);

      if (error.message?.includes('404')) {
        throw new Error('El profesor no existe.');
      }
      if (error.message?.includes('409')) {
        throw new Error('Ya existe un profesor con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el profesor.');
    }
  }

  async getProfesorById(profesorId) {
    if (!profesorId) {
      throw new Error('No se encontró el identificador del profesor.');
    }

    try {
      const response = await apiService.get(`/profesores/${profesorId}`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener el profesor.';
        throw new Error(msg);
      }

      const prof = response?.profesor || response?.Profesor || response;
      return prof;
    } catch (error) {
      console.error('❌ Error al obtener profesor por id:', error);

      if (error.message?.includes('404')) {
        throw new Error('El profesor no existe.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos cargar el profesor.');
    }
  }

  async createAlumno(payload = {}) {
    if (!payload.idRegional) {
      throw new Error('Debes seleccionar un regional para crear el alumno.');
    }

    try {
      const response = await apiService.post('/alumnos', payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos crear el alumno.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al crear alumno:', error);

      if (error.message?.includes('409')) {
        throw new Error('Ya existe un alumno con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos crear el alumno.');
    }
  }

  async updateAlumno(alumnoId, payload = {}) {
    if (!alumnoId) {
      throw new Error('No se encontró el identificador del alumno.');
    }
    if (!payload.idRegional) {
      throw new Error('Debes seleccionar un regional para actualizar el alumno.');
    }

    try {
      const response = await apiService.put(`/alumnos/${alumnoId}`, payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el alumno.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar alumno:', error);

      if (error.message?.includes('404')) {
        throw new Error('El alumno no existe.');
      }
      if (error.message?.includes('409')) {
        throw new Error('Ya existe un alumno con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el alumno.');
    }
  }

  async getAlumnosPorRegional(regionalId, options = {}) {
    if (!regionalId) {
      throw new Error('No hay un regional activo seleccionado.');
    }

    try {
      const params = new URLSearchParams();
      if (options.start !== undefined && options.start !== null) params.append('start', options.start);
      if (options.end !== undefined && options.end !== null) params.append('end', options.end);
      const qs = params.toString();
      const response = await apiService.get(`/alumnos/por-regional/${regionalId}${qs ? `?${qs}` : ''}`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener los alumnos.';
        throw new Error(msg);
      }

      const alumnos = response?.alumnos || response?.Alumnos || response?.data || response || [];
      const items = Array.isArray(alumnos) ? alumnos : [];
      const total = response?.total ?? response?.Total ?? items.length;
      if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
        return { items, total };
      }
      return items;
    } catch (error) {
      console.error('❌ Error al obtener alumnos por regional:', error);

      if (error.message?.includes('404')) {
        throw new Error('No se encontraron alumnos para este regional.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos cargar los alumnos de este regional.');
    }
  }

  async validateProfesorPorEmail(email, idRegional) {
    if (!email) {
      throw new Error('Debes ingresar un email para validar al profesor.');
    }
    if (!idRegional) {
      throw new Error('Debes seleccionar un regional para validar al profesor.');
    }

    try {
      const response = await apiService.post('/profesores/validar-por-mail', {
        email,
        idRegional
      });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || response.mensaje || 'No pudimos validar el profesor.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al validar profesor por email:', error);

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos validar el profesor.');
    }
  }

  async validateAlumnoPorEmail(email, idRegional) {
    if (!email) {
      throw new Error('Debes ingresar un email para validar al alumno.');
    }
    if (!idRegional) {
      throw new Error('Debes seleccionar un regional para validar al alumno.');
    }

    try {
      const response = await apiService.post('/alumnos/validar-por-mail', {
        email,
        idRegional
      });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || response.mensaje || 'No pudimos validar el alumno.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al validar alumno por email:', error);

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos validar el alumno.');
    }
  }

  async desasociarProfesorDeRegional(profesorId, idRegional) {
    if (!profesorId || !idRegional) {
      throw new Error('Faltan datos para desasociar al profesor del regional.');
    }

    try {
      const response = await apiService.request('/profesores/asociacion', {
        method: 'DELETE',
        body: { profesorId, idRegional },
      });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || response.mensaje || 'No pudimos desasociar al profesor.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al desasociar profesor:', error);

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos desasociar al profesor.');
    }
  }

  async desasociarAlumnoDeRegional(alumnoId, idRegional) {
    if (!alumnoId || !idRegional) {
      throw new Error('Faltan datos para desasociar al alumno del regional.');
    }

    try {
      const response = await apiService.request('/alumnos/asociacion', {
        method: 'DELETE',
        body: { alumnoId, idRegional },
      });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || response.mensaje || 'No pudimos desasociar al alumno.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al desasociar alumno:', error);

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos desasociar al alumno.');
    }
  }

  // ---------- Admin Área ----------

  async getAdminAreaUsersPorRegional(regionalId, options = {}) {
    if (!regionalId) {
      throw new Error('No hay un regional activo seleccionado.');
    }

    try {
      const params = new URLSearchParams();
      if (options.start !== undefined && options.start !== null) params.append('start', options.start);
      if (options.end !== undefined && options.end !== null) params.append('end', options.end);
      const qs = params.toString();
      const response = await apiService.get(`/adminareausers/por-regional/${regionalId}${qs ? `?${qs}` : ''}`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener los admins de área.';
        throw new Error(msg);
      }

      const usuarios = response?.usuarios || response?.Usuarios || response?.data || response || [];
      const items = Array.isArray(usuarios) ? usuarios : [];
      const total = response?.total ?? response?.Total ?? items.length;
      if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
        return { items, total };
      }
      return items;
    } catch (error) {
      console.error('❌ Error al obtener admins de área:', error);

      if (error.message?.includes('404')) {
        throw new Error('No se encontraron admins de área para este regional.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos cargar los admins de área.');
    }
  }

  async createAdminAreaUser(payload = {}) {
    if (!payload.idRegional) {
      throw new Error('Debes seleccionar un regional para crear el admin de área.');
    }

    try {
      const response = await apiService.post('/adminareausers', payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos crear el admin de área.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al crear admin de área:', error);

      if (error.message?.includes('409')) {
        throw new Error('Ya existe un admin de área con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos crear el admin de área.');
    }
  }

  async updateAdminAreaUser(adminAreaUserId, payload = {}) {
    if (!adminAreaUserId) {
      throw new Error('No se encontró el identificador del admin de área.');
    }
    if (!payload.idRegional) {
      throw new Error('Debes seleccionar un regional para actualizar el admin de área.');
    }

    try {
      const response = await apiService.put(`/adminareausers/${adminAreaUserId}`, payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el admin de área.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar admin de área:', error);

      if (error.message?.includes('404')) {
        throw new Error('El admin de área no existe.');
      }
      if (error.message?.includes('409')) {
        throw new Error('Ya existe un admin de área con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el admin de área.');
    }
  }

  async updateAdminAreaActivo(adminAreaUserId, activo) {
    if (!adminAreaUserId) {
      throw new Error('No se encontró el identificador del admin de área.');
    }

    try {
      const response = await apiService.put(`/adminareausers/${adminAreaUserId}/activo`, { activo });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el estado del admin de área.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar estado del admin de área:', error);

      if (error.message?.includes('404')) {
        throw new Error('El admin de área no existe.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el estado del admin de área.');
    }
  }

  async resetAdminAreaPassword(adminAreaUserId) {
    if (!adminAreaUserId) {
      throw new Error('No se encontró el identificador del admin de área.');
    }

    try {
      const response = await apiService.put(`/adminareausers/${adminAreaUserId}/reset-password`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos resetear la contraseña.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al resetear password de admin de área:', error);

      if (error.message?.includes('404')) {
        throw new Error('El admin de área no existe.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos resetear la contraseña.');
    }
  }

  // ---------- Admin Regional ----------

  async getAdminRegionalUsersPorRegional(regionalId, options = {}) {
    if (!regionalId) {
      throw new Error('No hay un regional seleccionado.');
    }

    try {
      const params = new URLSearchParams();
      if (options.start !== undefined && options.start !== null) params.append('start', options.start);
      if (options.end !== undefined && options.end !== null) params.append('end', options.end);
      const qs = params.toString();
      const response = await apiService.get(`/adminregionalusers/por-regional/${regionalId}${qs ? `?${qs}` : ''}`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos obtener los admins regionales.';
        throw new Error(msg);
      }

      const usuarios = response?.usuarios || response?.Usuarios || response?.data || response || [];
      const items = Array.isArray(usuarios) ? usuarios : [];
      const total = response?.total ?? response?.Total ?? items.length;
      if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
        return { items, total };
      }
      return items;
    } catch (error) {
      console.error('❌ Error al obtener admins regionales:', error);

      if (error.message?.includes('404')) {
        throw new Error('No se encontraron admins regionales para este regional.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos cargar los admins regionales.');
    }
  }

  async createAdminRegionalUser(payload = {}) {
    if (!payload.idRegional) {
      throw new Error('Debes seleccionar un regional para crear el admin regional.');
    }

    try {
      const response = await apiService.post('/adminregionalusers', payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos crear el admin regional.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al crear admin regional:', error);

      if (error.message?.includes('409')) {
        throw new Error('Ya existe un admin regional con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos crear el admin regional.');
    }
  }

  async updateAdminRegionalUser(adminRegionalUserId, payload = {}) {
    if (!adminRegionalUserId) {
      throw new Error('No se encontró el identificador del admin regional.');
    }
    if (!payload.idRegional) {
      throw new Error('Debes seleccionar un regional para actualizar el admin regional.');
    }

    try {
      const response = await apiService.put(`/adminregionalusers/${adminRegionalUserId}`, payload);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el admin regional.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar admin regional:', error);

      if (error.message?.includes('404')) {
        throw new Error('El admin regional no existe.');
      }
      if (error.message?.includes('409')) {
        throw new Error('Ya existe un admin regional con este email o legajo.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el admin regional.');
    }
  }

  async updateAdminRegionalActivo(adminRegionalUserId, activo) {
    if (!adminRegionalUserId) {
      throw new Error('No se encontró el identificador del admin regional.');
    }

    try {
      const response = await apiService.put(`/adminregionalusers/${adminRegionalUserId}/activo`, { activo });

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos actualizar el estado del admin regional.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar estado del admin regional:', error);

      if (error.message?.includes('404')) {
        throw new Error('El admin regional no existe.');
      }

      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos actualizar el estado del admin regional.');
    }
  }

  async resetAdminRegionalPassword(adminRegionalUserId) {
    if (!adminRegionalUserId) {
      throw new Error('No se encontró el identificador del admin regional.');
    }

    try {
      const response = await apiService.put(`/adminregionalusers/${adminRegionalUserId}/reset-password`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos resetear la contraseña.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al resetear password de admin regional:', error);

      if (error.message?.includes('404')) {
        throw new Error('El admin regional no existe.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos resetear la contraseña.');
    }
  }

  async resetProfesorPassword(profesorId) {
    if (!profesorId) {
      throw new Error('No se encontró el identificador del profesor.');
    }

    try {
      const response = await apiService.put(`/profesores/${profesorId}/reset-password`);

      if (response && typeof response.ok !== 'undefined' && !response.ok) {
        const msg = response.message || response.error || response.title || 'No pudimos resetear la contraseña.';
        throw new Error(msg);
      }

      return response;
    } catch (error) {
      console.error('❌ Error al resetear password de profesor:', error);

      if (error.message?.includes('404')) {
        throw new Error('El profesor no existe.');
      }
      if (error.message?.includes('401')) {
        throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
      }

      throw new Error(error.message || 'No pudimos resetear la contraseña.');
    }
  }
}

// Crear instancia singleton
const usuarioService = new UsuarioService();

export default usuarioService;
